import { Adjustments, ColorProfileName, LookTable, Mask, MaskGroup } from "@/core/types"
import { decodeLookTable } from "@/core/dcp/look-table"
import { radians } from "./math"
import { dist } from "@/utils/math"
import {
	createBackgroundMask,
	createGradientMask,
	createSubjectMask,
	createMaskGroup,
	createRadialMask,
	createSkyMask,
} from "@/store/editor/mask-utils"
import { isAdjustmentKey } from "@/core/adjustments"

export interface CameraRawSettingsConversionResult {
	adjustments: Partial<Adjustments>
	userLut?: LookTable
}

// TODO: define individual properties
export type CameraRawSettings = Record<string, string | number | Array<[number, number]>> & {
	LookTable?: LookTable
	LocalAdjustments?: MaskGroup[]
}

export const propertyMap: Record<string, keyof Adjustments> = {
	// "AsShotTemperature": "6100",
	// "AsShotTint": "3",
	Temperature: "temperature",
	Tint: "tint",
	Exposure2012: "exposure",
	Contrast2012: "contrast",
	Highlights2012: "highlights",
	Shadows2012: "shadows",
	Whites2012: "whites",
	Blacks2012: "blacks",
	Texture: "texture",
	Clarity2012: "clarity",
	Dehaze: "dehaze",
	Vibrance: "vibrance",
	Saturation: "saturation",

	ParametricShadows: "parametricShadows",
	ParametricDarks: "parametricDarks",
	ParametricLights: "parametricLights",
	ParametricHighlights: "parametricHighlights",
	ParametricShadowSplit: "parametricDarksStop",
	ParametricMidtoneSplit: "parametricMidsStop",
	ParametricHighlightSplit: "parametricLightsStop",

	// "Sharpness": "30",
	// "SharpenRadius": "+0.7",
	// "SharpenDetail": "25",
	// "SharpenEdgeMasking": "0",

	// "LuminanceSmoothing": "20",
	// "LuminanceNoiseReductionDetail": "50",
	// "LuminanceNoiseReductionContrast": "0",

	// "ColorNoiseReduction": "25",
	// "ColorNoiseReductionDetail": "50",
	// "ColorNoiseReductionSmoothness": "50",

	GrayMixerRed: "luminanceRed",
	GrayMixerOrange: "luminanceOrange",
	GrayMixerYellow: "luminanceYellow",
	GrayMixerGreen: "luminanceGreen",
	GrayMixerAqua: "luminanceAqua",
	GrayMixerBlue: "luminanceBlue",
	GrayMixerPurple: "luminancePurple",
	GrayMixerMagenta: "luminanceMagenta",

	HueAdjustmentRed: "hueRed",
	HueAdjustmentOrange: "hueOrange",
	HueAdjustmentYellow: "hueYellow",
	HueAdjustmentGreen: "hueGreen",
	HueAdjustmentAqua: "hueAqua",
	HueAdjustmentBlue: "hueBlue",
	HueAdjustmentPurple: "huePurple",
	HueAdjustmentMagenta: "hueMagenta",
	SaturationAdjustmentRed: "saturationRed",
	SaturationAdjustmentOrange: "saturationOrange",
	SaturationAdjustmentYellow: "saturationYellow",
	SaturationAdjustmentGreen: "saturationGreen",
	SaturationAdjustmentAqua: "saturationAqua",
	SaturationAdjustmentBlue: "saturationBlue",
	SaturationAdjustmentPurple: "saturationPurple",
	SaturationAdjustmentMagenta: "saturationMagenta",
	LuminanceAdjustmentRed: "luminanceRed",
	LuminanceAdjustmentOrange: "luminanceOrange",
	LuminanceAdjustmentYellow: "luminanceYellow",
	LuminanceAdjustmentGreen: "luminanceGreen",
	LuminanceAdjustmentAqua: "luminanceAqua",
	LuminanceAdjustmentBlue: "luminanceBlue",
	LuminanceAdjustmentPurple: "luminancePurple",
	LuminanceAdjustmentMagenta: "luminanceMagenta",
	SplitToningShadowHue: "gradingShadowsHue",
	SplitToningShadowSaturation: "gradingShadowsSaturation",
	SplitToningHighlightHue: "gradingHighlightsHue",
	SplitToningHighlightSaturation: "gradingHighlightsSaturation",
	SplitToningBalance: "gradingBalance",
	ColorGradeMidtoneHue: "gradingMidtonesHue",
	ColorGradeMidtoneSat: "gradingMidtonesSaturation",
	ColorGradeShadowLum: "gradingShadowsLuminance",
	ColorGradeMidtoneLum: "gradingMidtonesLuminance",
	ColorGradeHighlightLum: "gradingHighlightsLuminance",
	ColorGradeGlobalHue: "gradingGlobalHue",
	ColorGradeGlobalSat: "gradingGlobalSaturation",
	ColorGradeGlobalLum: "gradingGlobalLuminance",
	ColorGradeBlending: "gradingBlending",

	// "AutoLateralCA": "0",
	// "LensManualDistortionAmount": "0",
	// "VignetteAmount": "0",
	// "DefringePurpleAmount": "0",
	// "DefringePurpleHueLo": "30",
	// "DefringePurpleHueHi": "70",
	// "DefringeGreenAmount": "0",
	// "DefringeGreenHueLo": "40",
	// "DefringeGreenHueHi": "60",

	// "GrainAmount": "15",
	// "GrainSize": "46",
	// "GrainFrequency": "37",

	PostCropVignetteAmount: "vignette",
	PostCropVignetteFeather: "vignetteFeather",
	// "PostCropVignetteMidpoint": "0",
	// "PostCropVignetteRoundness": "0",
	// "PostCropVignetteStyle": "1",
	// "PostCropVignetteHighlightContrast": "0",

	// "ShadowTint": "+1",
	RedHue: "calibrationRedHue",
	RedSaturation: "calibrationRedSat",
	GreenHue: "calibrationGreenHue",
	GreenSaturation: "calibrationGreenSat",
	BlueHue: "calibrationBlueHue",
	BlueSaturation: "calibrationBlueSat",

	// "ConvertToGrayscale": "False",
	// "OverrideLookVignette": "False",
	// "ToneCurveName2012": "Custom",
	// "CropConstrainToWarp": "0",

	ColorProfile: "colorProfile",

	ToneCurvePV2012: "curvesAll",
	ToneCurvePV2012Red: "curvesRed",
	ToneCurvePV2012Green: "curvesGreen",
	ToneCurvePV2012Blue: "curvesBlue",

	LocalAdjustments: "localAdjustments",
}

export const propertyParsers = {
	CameraProfile: parseValue,
	Version: parseNumber,
	ProcessVersion: parseNumber,
	AsShotTemperature: parseNumber,
	AsShotTint: parseNumber,
	Temperature: parseNumber,
	Tint: parseNumber,
	Exposure2012: parseNumber,
	Contrast2012: parseNumberNormalized,
	Highlights2012: parseNumberNormalized,
	Shadows2012: parseNumberNormalized,
	Whites2012: parseNumberNormalized,
	Blacks2012: parseNumberNormalized,
	Texture: parseNumberNormalized,
	Clarity2012: parseNumberNormalized,
	Dehaze: parseNumberNormalized,
	Vibrance: parseNumberNormalized,
	Saturation: parseNumberNormalized,
	GrayMixerRed: parseNumberNormalized,
	GrayMixerOrange: parseNumberNormalized,
	GrayMixerYellow: parseNumberNormalized,
	GrayMixerGreen: parseNumberNormalized,
	GrayMixerAqua: parseNumberNormalized,
	GrayMixerBlue: parseNumberNormalized,
	GrayMixerPurple: parseNumberNormalized,
	GrayMixerMagenta: parseNumberNormalized,
	HueAdjustmentRed: parseNumberNormalized,
	HueAdjustmentOrange: parseNumberNormalized,
	HueAdjustmentYellow: parseNumberNormalized,
	HueAdjustmentGreen: parseNumberNormalized,
	HueAdjustmentAqua: parseNumberNormalized,
	HueAdjustmentBlue: parseNumberNormalized,
	HueAdjustmentPurple: parseNumberNormalized,
	HueAdjustmentMagenta: parseNumberNormalized,
	SaturationAdjustmentRed: parseNumberNormalized,
	SaturationAdjustmentOrange: parseNumberNormalized,
	SaturationAdjustmentYellow: parseNumberNormalized,
	SaturationAdjustmentGreen: parseNumberNormalized,
	SaturationAdjustmentAqua: parseNumberNormalized,
	SaturationAdjustmentBlue: parseNumberNormalized,
	SaturationAdjustmentPurple: parseNumberNormalized,
	SaturationAdjustmentMagenta: parseNumberNormalized,
	LuminanceAdjustmentRed: parseNumberNormalized,
	LuminanceAdjustmentOrange: parseNumberNormalized,
	LuminanceAdjustmentYellow: parseNumberNormalized,
	LuminanceAdjustmentGreen: parseNumberNormalized,
	LuminanceAdjustmentAqua: parseNumberNormalized,
	LuminanceAdjustmentBlue: parseNumberNormalized,
	LuminanceAdjustmentPurple: parseNumberNormalized,
	LuminanceAdjustmentMagenta: parseNumberNormalized,
	ParametricShadows: parseNumberNormalized,
	ParametricDarks: parseNumberNormalized,
	ParametricLights: parseNumberNormalized,
	ParametricHighlights: parseNumberNormalized,
	ParametricShadowSplit: parseNumberNormalized,
	ParametricMidtoneSplit: parseNumberNormalized,
	ParametricHighlightSplit: parseNumberNormalized,
	SplitToningShadowHue: parseHue,
	SplitToningShadowSaturation: parseNumberNormalized,
	SplitToningHighlightHue: parseHue,
	SplitToningHighlightSaturation: parseNumberNormalized,
	SplitToningBalance: parseNumberNormalized,
	ColorGradeMidtoneHue: parseHue,
	ColorGradeMidtoneSat: parseNumberNormalized,
	ColorGradeShadowLum: parseNumberNormalized,
	ColorGradeMidtoneLum: parseNumberNormalized,
	ColorGradeHighlightLum: parseNumberNormalized,
	ColorGradeBlending: parseNumberNormalized,
	ColorGradeGlobalHue: parseHue,
	ColorGradeGlobalSat: parseNumberNormalized,
	ColorGradeGlobalLum: parseNumberNormalized,
	PostCropVignetteAmount: parseNumberNormalized,
	PostCropVignetteFeather: parseNumberNormalized,
	ShadowTint: parseNumberNormalized,
	RedHue: parseNumberNormalized,
	RedSaturation: parseNumberNormalized,
	GreenHue: parseNumberNormalized,
	GreenSaturation: parseNumberNormalized,
	BlueHue: parseNumberNormalized,
	BlueSaturation: parseNumberNormalized,

	LocalExposure2012: parseLocalExposure,
	LocalTint: parseLocalTint,
}

function parseNumber(val: string | undefined) {
	const result = Number(val)

	if (!result || Number.isNaN(result)) {
		return 0
	}
	return result
}

function parseNumberNormalized(val: string) {
	return parseNumber(val) / 100
}

function parseHue(val: string) {
	return parseNumber(val) / 360
}

function parseLocalExposure(val: string) {
	return parseNumberWithRange(val, 4)
}

function parseLocalTint(val: string) {
	return parseNumberWithRange(val, 100)
}

function parseNumberWithRange(val: string, targetRange: number) {
	return parseNumber(val) * targetRange
}

export function parseValue(value: string) {
	if (value === "True") {
		return true
	}

	if (value === "False") {
		return false
	}

	return value
}

export function parseName(crs: CameraRawSettings, root: Element) {
	const nameTag = root.getElementsByTagName(`crs:Name`)[0]
	const groupTag = root.getElementsByTagName(`crs:Group`)[0]
	const name = nameTag?.getElementsByTagName(`rdf:li`)[0]?.textContent
	const group = groupTag?.getElementsByTagName(`rdf:li`)[0]?.textContent

	if (name) {
		crs.Name = name
	}

	if (group) {
		crs.Group = group
	}
}

function parseMask(maskAttributes: NamedNodeMap): Mask | undefined {
	const type = parseMaskType(maskAttributes)
	if (!type) return undefined

	switch (type) {
		case "radial":
			return parseRadialMask(maskAttributes)
		case "gradient":
			return parseGradientMask(maskAttributes)
		case "background":
			return createBackgroundMask({
				...parseBaseMask(maskAttributes),
				type,
			})
		case "subject":
			return createSubjectMask({
				...parseBaseMask(maskAttributes),
				type,
			})
		case "sky":
			return createSkyMask({
				...parseBaseMask(maskAttributes),
				type,
			})
	}
}

function parseMaskType(maskAttributes: NamedNodeMap) {
	const type = maskAttributes.getNamedItem("crs:What")
	switch (type?.value) {
		case "Mask/Image": {
			const subType = maskAttributes.getNamedItem("crs:MaskSubType")
			const subCategory = maskAttributes.getNamedItem("crs:MaskSubCategoryID")
			switch (subType?.value) {
				case "0": {
					// AI types including background, subject, sky, person
					switch (subCategory?.value) {
						case "22": // Background
							return "background"
						case "23": // Subject
						case "20036": // Person
							return "subject"
					}
					return "background" // Default to background if no specific type
				}

				case "1": // subject
				case "3": // person
					return "subject"
				case "2":
					return "sky"
			}
			break
		}
		case "Mask/Gradient": {
			return "gradient"
		}
		case "Mask/CircularGradient": {
			return "radial"
		}
	}
	return undefined
}

function parseBaseMask(maskAttributes: NamedNodeMap) {
	const mask: Partial<Mask> = {}
	for (let attributeIndex = 0; attributeIndex < maskAttributes.length; attributeIndex++) {
		const attr = maskAttributes[attributeIndex]
		if (!attr) continue
		const { localName, value } = attr

		switch (localName) {
			case "MaskSyncID":
				mask.id = value
				break
			case "MaskName":
				mask.name = value
				break
			case "MaskBlendMode":
				mask.blendMode = value === "0" ? "add" : "subtract"
				break
			case "MaskInverted":
				mask.inverted = value.toLowerCase() === "true"
				break
			case "MaskActive":
				mask.active = value.toLowerCase() === "true"
				break
		}
	}
	return mask
}

function parseRadialMask(maskAttributes: NamedNodeMap) {
	const baseMask = parseBaseMask(maskAttributes)
	const left = parseNumber(maskAttributes.getNamedItem("crs:Left")?.value)
	const right = parseNumber(maskAttributes.getNamedItem("crs:Right")?.value)
	const top = parseNumber(maskAttributes.getNamedItem("crs:Top")?.value)
	const bottom = parseNumber(maskAttributes.getNamedItem("crs:Bottom")?.value)
	const angle = parseNumber(maskAttributes.getNamedItem("crs:Angle")?.value)

	const feather = parseNumber(maskAttributes.getNamedItem("crs:Feather")?.value)

	return createRadialMask({
		...baseMask,
		...convertLRRadialMask(left, top, right, bottom, angle, feather),
		type: "radial",
	})
}

function parseGradientMask(maskAttributes: NamedNodeMap) {
	const baseMask = parseBaseMask(maskAttributes)

	// In LR, the XY-axis are inverted
	return createGradientMask({
		...baseMask,
		startX: parseNumber(maskAttributes.getNamedItem("crs:FullX")?.value),
		startY: parseNumber(maskAttributes.getNamedItem("crs:FullY")?.value),
		endX: parseNumber(maskAttributes.getNamedItem("crs:ZeroX")?.value),
		endY: parseNumber(maskAttributes.getNamedItem("crs:ZeroY")?.value),
		type: "gradient",
	})
}

export function parseMasks(crs: CameraRawSettings, root: Element) {
	const maskTag = root.getElementsByTagName(`crs:MaskGroupBasedCorrections`)[0]
	const maskAdjustments = maskTag?.getElementsByTagName(`rdf:Description`)
	const masks = maskTag?.getElementsByTagName(`crs:CorrectionMasks`)

	if (!masks || !maskAdjustments || maskAdjustments.length != masks.length) {
		return
	}

	const parsedMaskGroups: MaskGroup[] = []

	for (let i = 0; i < masks.length; i++) {
		const attributes = maskAdjustments[i]?.attributes

		if (!attributes) continue

		const maskGroup: MaskGroup = createMaskGroup()

		//Parsing adjustments
		for (let attributeIndex = 0; attributeIndex < attributes.length; attributeIndex++) {
			const attr = attributes[attributeIndex]
			if (!attr) continue

			const { localName, value } = attr

			switch (localName) {
				case "CorrectionSyncID":
					maskGroup.id = value
					break
				case "CorrectionName":
					maskGroup.name = value
					break
				case "CorrectionActive":
					maskGroup.active = value.toLowerCase() === "true"
					break
				case "CorrectionAmount":
					maskGroup.strength = parseNumber(value)
					break
				default: {
					const parser: ((value: string) => any) | undefined = propertyParsers[localName]
					const adjustmentsKey = propertyMap[localName.replace("Local", "")]
					if (!adjustmentsKey) continue
					maskGroup[adjustmentsKey] = parser ? parser(value) : parseNumber(value) //local adjustment values are already normalized
					break
				}
			}
		}

		//Parsing masks
		const maskList = masks[i]?.getElementsByTagName(`rdf:li`)
		const parsedMasks: Mask[] = []
		if (!maskList) continue
		for (let maskIndex = 0; maskIndex < maskList.length; maskIndex++) {
			const maskAttributes = maskList[maskIndex]?.attributes
			if (!maskAttributes) continue

			const mask = parseMask(maskAttributes)
			if (!mask) continue
			parsedMasks.push(mask)
		}

		maskGroup.masks = parsedMasks
		parsedMaskGroups.push(maskGroup)
	}

	crs.LocalAdjustments = parsedMaskGroups
}

function convertLRRadialMask(
	left: number,
	top: number,
	right: number,
	bottom: number,
	angle: number,
	feather: number,
) {
	const centerX = (left + right) / 2
	const centerY = (top + bottom) / 2
	const x = right - left
	const y = bottom - top
	const r = dist(left, top, right, bottom) / 2
	const phi = Math.atan2(y, x)
	const theta = phi - radians(angle)
	const radiusX = r * Math.cos(theta)
	const radiusY = r * Math.sin(theta)

	// Our angle=positive means counter-clock-wise, while LR is opposite.
	return { centerX, centerY, radiusX, radiusY, angle: -angle, feather: feather / 100 }
}

export function parseToneCurveSettings(crs: CameraRawSettings, root: Element, localName: string) {
	const toneCurveTag = root.getElementsByTagName(`crs:${localName}`)[0]
	const seq = toneCurveTag?.getElementsByTagName("rdf:li")

	if (!seq || seq.length < 2) {
		return
	}

	const points: Array<[number, number]> = []

	for (let i = 0; i < seq.length; i++) {
		const [x, y] = seq[i]?.textContent?.split(",").map(parseNumber) ?? [0, 0]

		points.push([x ?? 0, y ?? 0])
	}

	crs[localName] = points
}

function parseCameraProfile(crs: CameraRawSettings) {
	const profile = crs["CameraProfile"]

	if (typeof profile !== "string") {
		return
	}

	crs["ColorProfile"] = getColorProfileNameFromString(profile)
}

export function parseLookTable(crs: CameraRawSettings) {
	const lookTableId = crs["LookTable"] ?? crs["RGBTable"]
	const lookTable = crs[`Table_${lookTableId}`]

	if (typeof lookTable !== "string") {
		return
	}

	crs["LookTable"] = decodeLookTable(lookTable)

	delete crs[`Table_${lookTableId}`]
}

export function parseColorProfile(crs: CameraRawSettings, doc: Document) {
	if (typeof crs["ConvertToGrayscale"] === "boolean" && crs["ConvertToGrayscale"] === true) {
		// Special case for 'older' presets from original color profiles
		crs["ColorProfile"] = ColorProfileName.StandardMonochrome
		return
	}

	const lookRoot = doc.getElementsByTagName("crs:Look")[0] as Element

	if (!lookRoot) {
		return
	}

	const lookDescriptionRoot = lookRoot.getElementsByTagName("rdf:Description")[0] as Element

	if (!lookDescriptionRoot) {
		return
	}

	for (let i = 0; i < lookDescriptionRoot.attributes.length; i++) {
		const attr = lookDescriptionRoot.attributes[i]

		if (!attr) continue

		const { prefix, localName, value } = attr

		if (prefix === "crs" && localName === "Name") {
			crs["ColorProfile"] = getColorProfileNameFromString(value)
			break
		}
	}
}

export function parseCameraRawSettings(xml: string) {
	const domParser = new DOMParser()
	const doc = domParser.parseFromString(xml, "application/xml")
	const root = doc.getElementsByTagName("rdf:Description")[0] as Element

	if (!root) {
		return {}
	}

	const crs: CameraRawSettings = {}

	for (let i = 0; i < root.attributes.length; i++) {
		const attr = root.attributes[i]

		if (!attr) continue

		const { prefix, localName, value } = attr
		const parser: ((value: string) => any) | undefined = propertyParsers[localName]

		if (prefix === "crs") {
			crs[localName] = parser ? parser(value) : parseValue(value)
		}
	}

	parseToneCurveSettings(crs, root, "ToneCurvePV2012")
	parseToneCurveSettings(crs, root, "ToneCurvePV2012Red")
	parseToneCurveSettings(crs, root, "ToneCurvePV2012Green")
	parseToneCurveSettings(crs, root, "ToneCurvePV2012Blue")
	parseMasks(crs, root)

	parseName(crs, root)

	parseCameraProfile(crs)
	parseColorProfile(crs, doc)

	// TODO: Parse look table but DO NOT store in adjustments!
	// It is too large to store in adjustments.
	// Instead use some kind of storage/caching layer and then put an identifier/url inside adjustments
	parseLookTable(crs)

	return crs
}

export function convertCameraRawSetting(crs: CameraRawSettings): CameraRawSettingsConversionResult {
	const adjustments: Partial<Adjustments> = {}

	Object.entries(crs).forEach(([key, value]) => {
		const adjustmentsKey = propertyMap[key]
		if (adjustmentsKey && isAdjustmentKey(adjustmentsKey)) {
			// @ts-expect-error type checker cannot rule out adjustments[adjustmentsKey] === undefined
			adjustments[adjustmentsKey] = value
		}
	})

	if (crs["LookTable"]) {
		adjustments.userLutId = crs["LookTable"].id
	}

	return {
		adjustments,
		userLut: crs["LookTable"],
	}
}

export function isColorProfileMonochrome(name: ColorProfileName | null): boolean {
	return name === ColorProfileName.Monochrome || name === ColorProfileName.StandardMonochrome
}

function getColorProfileNameFromString(value: string): ColorProfileName {
	let profileName: ColorProfileName

	switch (value) {
		case "Adobe Standard":
			profileName = ColorProfileName.Standard
			break
		case "Adobe Monochrome":
			profileName = ColorProfileName.Monochrome
			break
		default:
			profileName = ColorProfileName.Color
	}

	return profileName
}
